// Frontend API client. Talks to the Pages Functions backend.

import type { RegionalSummary, RiverAnalysis, RiverCategory } from '../../functions/_lib/types';

const API_BASE = '/api';

export type RiversResponse = { rivers: RiverAnalysis[]; generated_at: string };

export type RiversMeta = {
  generated_at: string;
  age_minutes: number;
};

export type ApiError = {
  kind: '503' | 'network' | 'http';
  message: string;
  retry_after_s?: number;
};

export type RiversResult = {
  data: RiversResponse | null;
  meta: RiversMeta | null;
  error: ApiError | null;
};

export async function fetchRivers(): Promise<RiversResult> {
  try {
    const res = await fetch(`${API_BASE}/rivers`);
    if (res.status === 503) {
      const retryAfter = res.headers.get('retry-after');
      return {
        data: null, meta: null,
        error: {
          kind: '503',
          message: 'Snapshot rebuild in progress',
          retry_after_s: retryAfter ? Number(retryAfter) : undefined,
        },
      };
    }
    if (!res.ok) {
      return {
        data: null, meta: null,
        error: { kind: 'http', message: `/api/rivers ${res.status}` },
      };
    }
    const generatedAt = res.headers.get('x-snapshot-generated-at') ?? new Date().toISOString();
    const ageMin = Math.max(
      0,
      Math.round((Date.now() - new Date(generatedAt).getTime()) / 60_000),
    );
    const body = (await res.json()) as RiversResponse;
    return {
      data: body,
      meta: { generated_at: generatedAt, age_minutes: ageMin },
      error: null,
    };
  } catch (e) {
    return {
      data: null, meta: null,
      error: { kind: 'network', message: (e as Error).message },
    };
  }
}

export async function fetchSummary(category: RiverCategory | 'combined'): Promise<RegionalSummary> {
  const res = await fetch(`${API_BASE}/summary?category=${category}`);
  if (!res.ok) throw new Error(`/api/summary failed: ${res.status}`);
  return (await res.json()) as RegionalSummary;
}

export async function fetchRiverDetail(id: string): Promise<RiverAnalysis> {
  const res = await fetch(`${API_BASE}/rivers/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`/api/rivers/${id} failed: ${res.status}`);
  return (await res.json()) as RiverAnalysis;
}

// ── Devotional blog ─────────────────────────────────────────────────

export interface DevotionalMeta {
  slug: string;
  title: string;
  primary_scripture: string;
  season: string;
  activity_bucket: string;
  excerpt: string;
  description?: string;
  word_count: number;
  read_time: number;
  tags?: string[];
  content_type?: string;
  story_when?: string | null;
  story_when_precision?: string;
  story_when_label?: string;
  series_role?: string;
  see_also?: string;
}

export interface DevotionalFull extends DevotionalMeta {
  html: string;
}

let _indexCache: DevotionalMeta[] | null = null;

export type ApiResult<T> =
  | { data: T; error: null }
  | { data: null; error: { message: string } };

export async function fetchDevotionalIndex(): Promise<ApiResult<DevotionalMeta[]>> {
  if (_indexCache) return { data: _indexCache, error: null };
  try {
    const res = await fetch('/data/devotionals-index.json');
    if (!res.ok) {
      return { data: null, error: { message: `Index responded ${res.status}` } };
    }
    _indexCache = (await res.json()) as DevotionalMeta[];
    return { data: _indexCache, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}

/**
 * Returns 3-state result for an entry fetch:
 *   { data: <entry>, error: null }      — found
 *   { data: null,    error: null }      — 404 / legit not-found
 *   { data: null,    error: { message } } — fetch failure
 */
export async function fetchDevotionalEntry(slug: string): Promise<ApiResult<DevotionalFull | null>> {
  try {
    const res = await fetch(`/data/devotionals/${encodeURIComponent(slug)}.json`);
    if (res.status === 404) {
      return { data: null, error: null };
    }
    if (!res.ok) {
      return { data: null, error: { message: `Entry responded ${res.status}` } };
    }
    return { data: (await res.json()) as DevotionalFull, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}
